import { isNil } from "@clipboard-health/util-ts";

import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

/**
 * Appends `maximum-scale=1` to the viewport meta inside the native webview so
 * the page scale can't get stuck above 1.
 *
 * WKWebView programmatically zooms the page when a focused text input renders
 * below ~16px and does not reliably restore the previous scale after the
 * keyboard is dismissed, leaving the whole app zoomed in. Capacitor already
 * disables the pinch-to-zoom gesture natively (`zoomEnabled` defaults to
 * false), but that doesn't stop WebKit's programmatic input-focus zoom —
 * capping the viewport scale does, so this removes no zoom capability users
 * have today. Android doesn't auto-zoom on input focus; it is included via
 * `Platform.isMobile` for platform parity. OS-level accessibility zoom is
 * unaffected, and web keeps pinch-to-zoom (the meta is never touched there).
 *
 * No-op if the viewport meta already declares a `maximum-scale`.
 */
export function initializeViewportZoomLock(): void {
  if (!Platform.isMobile) {
    return;
  }

  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (isNil(viewportMeta)) {
    logError(APP_V2_APP_EVENTS.VIEWPORT_ZOOM_LOCK_FAILURE, {
      error: new Error("viewport meta tag not found"),
    });
    return;
  }

  const content = viewportMeta.getAttribute("content") ?? "";
  if (content.includes("maximum-scale")) {
    return;
  }

  viewportMeta.setAttribute(
    "content",
    content === "" ? "maximum-scale=1" : `${content}, maximum-scale=1`
  );
}
